import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Button } from '@mui/material';
import { Done as DoneIcon, Code as CodeIcon } from '@mui/icons-material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { classes } from './styles';
import { LIST_ITEMS_REGEX } from './sharedConfig';
import { useHighlightOnNewItems } from '../../NewUI/hooks';

const normalize = (str: string) => {
  return str
    .replace(LIST_ITEMS_REGEX, '')
    .split('**')
    .slice(0, -1)
    .map((str) => str.trim());
};

interface CopyableQuestionsProps {
  questions: string | undefined;
  numberOfQuestions?: number;
  maxHeight?: string;
  newQuestions?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const CopyableQuestions: React.FC<CopyableQuestionsProps> = ({
  questions,
  numberOfQuestions,
  maxHeight,
  newQuestions = '',
  containerRef
}) => {
  const questionList = useMemo(() => (questions ? normalize(questions) : []), [questions]);
  const cleanedNewQuestions = useMemo(
    () => (newQuestions ? normalize(newQuestions) : []),
    [newQuestions]
  );

  const arrayOfBooleans = Array.from(Array(numberOfQuestions || questionList.length), () => false);
  const [copiedQuestion, setCopiedQuestion] = useState(arrayOfBooleans);
  const highlightedQuestions = useHighlightOnNewItems(cleanedNewQuestions);

  const handleCopyQuestion = useCallback((index: number) => {
    setCopiedQuestion((prev) => {
      const newState = new Array(prev.length).fill(false);
      newState[index] = true;
      return newState;
    });

    const timeoutId = setTimeout(() => {
      setCopiedQuestion((prev) => {
        const newState = [...prev];
        newState[index] = false;
        return newState;
      });
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!numberOfQuestions) {
      setCopiedQuestion(Array.from(Array(questionList.length), () => false));
    }
  }, [questions, numberOfQuestions, questionList.length]);

  return (
    <Box sx={{ ...classes.questionsContainer, ...(maxHeight && { maxHeight }) }} ref={containerRef}>
      {questionList.map((item, index) => {
        const isCopied = copiedQuestion[Number(index)];
        const isHighlighted = highlightedQuestions.includes(item);

        return (
          <Box
            sx={{
              ...classes.question,
              ...(isHighlighted && {
                ...classes.questionActive
              })
            }}
            key={index}
            data-highlighted={isHighlighted}
          >
            <Box sx={{ maxWidth: '700px' }}>{item}</Box>
            <CopyToClipboard text={item} onCopy={() => handleCopyQuestion(index)}>
              <Button
                className="copy-question-button"
                sx={{
                  ...classes.copyButton,
                  ...(isHighlighted && classes.copyButtonActive)
                }}
                endIcon={isCopied ? <DoneIcon /> : <CodeIcon />}
              >
                {isCopied ? 'Copied!' : 'Copy'}
              </Button>
            </CopyToClipboard>
          </Box>
        );
      })}
    </Box>
  );
};

export default CopyableQuestions;
